export interface Vec2Like { x: number; z: number }

export function distance2D(a: Vec2Like, b: Vec2Like): number {
  return Math.hypot(b.x - a.x, b.z - a.z);
}

export function normalize2D(v: Vec2Like): Vec2Like {
  const length = Math.hypot(v.x, v.z);
  return length < 1e-8 ? { x: 0, z: 1 } : { x: v.x / length, z: v.z / length };
}

export function forwardFromYaw(yaw: number): Vec2Like {
  return { x: Math.sin(yaw), z: Math.cos(yaw) };
}

export function angleBetweenDegrees(a: Vec2Like, b: Vec2Like): number {
  const an = normalize2D(a);
  const bn = normalize2D(b);
  const dot = Math.max(-1, Math.min(1, an.x * bn.x + an.z * bn.z));
  return Math.acos(dot) * 180 / Math.PI;
}

export function angleFromMonsterFront(
  monsterPosition: Vec2Like,
  monsterYaw: number,
  targetPosition: Vec2Like,
): number {
  return angleBetweenDegrees(forwardFromYaw(monsterYaw), {
    x: targetPosition.x - monsterPosition.x,
    z: targetPosition.z - monsterPosition.z,
  });
}

export function isInMouthAttackZone(
  monsterPosition: Vec2Like,
  monsterYaw: number,
  playerPosition: Vec2Like,
  attackRange: number,
  fullConeDegrees: number,
): boolean {
  return distance2D(monsterPosition, playerPosition) <= attackRange
    && angleFromMonsterFront(monsterPosition, monsterYaw, playerPosition) <= fullConeDegrees / 2;
}

export function isInKickZone(
  monsterPosition: Vec2Like,
  monsterYaw: number,
  playerPosition: Vec2Like,
  kickRange: number,
  rearSectorDegrees: number,
): boolean {
  if (distance2D(monsterPosition, playerPosition) > kickRange) return false;
  const rearHalfAngle = rearSectorDegrees / 2;
  const frontAngle = angleFromMonsterFront(monsterPosition, monsterYaw, playerPosition);
  return frontAngle >= 180 - rearHalfAngle;
}

export function shortestAngleDelta(from: number, to: number): number {
  return Math.atan2(Math.sin(to - from), Math.cos(to - from));
}

export function rotateToward(current: number, target: number, maxRadians: number): number {
  const delta = shortestAngleDelta(current, target);
  if (Math.abs(delta) <= maxRadians) return target;
  return current + Math.sign(delta) * maxRadians;
}
